import type { ResolvedBetterstartConfig } from '@core-engine/config/types.js'
import type { FormSchema, GeneratedFile, Schema } from '@core-engine/schema/types.js'
import {
  BARREL_SCOPE,
  hashContent,
  hasSnapshotFiles,
  manifestFilesByPath,
  readSnapshotFiles,
  type SnapshotManifest,
  writeSnapshotFiles
} from '@core-engine/snapshots/store.js'
import { runFormPipeline } from '@next-adapter/generators/form-pipeline/index.js'
import { runEntityPipeline, runSinglePipeline } from '@next-adapter/generators/index.js'
import { renderBarrelFiles } from '@next-adapter/snapshots/barrels.js'

function renderManifestFiles(
  cwd: string,
  config: ResolvedBetterstartConfig,
  manifest: SnapshotManifest
): GeneratedFile[] {
  if (manifest.schemaName === BARREL_SCOPE) {
    return renderBarrelFiles(cwd, config)
  }

  const schema = manifest.schemaJson
  if ('type' in schema && schema.type === 'form') {
    return runFormPipeline(schema as FormSchema, cwd, config, {
      force: true,
      silent: true
    }).files
  }

  if ('type' in schema && schema.type === 'single') {
    return runSinglePipeline(schema as Schema, cwd, config, {
      force: true,
      silent: true
    }).files
  }

  return runEntityPipeline(schema as Schema, cwd, config, {
    force: true,
    silent: true
  }).files
}

export interface ReconstructionResult {
  files: GeneratedFile[]
  mismatchedPaths: string[]
  trackedPaths: string[]
}

export function reconstructSnapshotFiles(
  cwd: string,
  config: ResolvedBetterstartConfig,
  manifest: SnapshotManifest
): ReconstructionResult {
  const trackedPaths = manifest.files.map((file) => file.path).sort()

  if (hasSnapshotFiles(cwd, manifest.schemaName)) {
    return {
      files: readSnapshotFiles(cwd, manifest.schemaName, manifest),
      mismatchedPaths: [],
      trackedPaths
    }
  }

  const renderedFiles = renderManifestFiles(cwd, config, manifest).filter(
    (file) => !manifest.skipped.includes(file.path)
  )
  const manifestFiles = manifestFilesByPath(manifest)
  const validFiles: GeneratedFile[] = []
  const mismatchedPaths: string[] = []

  for (const file of renderedFiles) {
    const manifestFile = manifestFiles.get(file.path)
    if (!manifestFile || manifestFile.hash !== hashContent(file.content)) {
      mismatchedPaths.push(file.path)
      continue
    }
    validFiles.push(file)
  }

  if (validFiles.length > 0) {
    writeSnapshotFiles(cwd, manifest.schemaName, validFiles)
  }

  return {
    files: validFiles,
    mismatchedPaths,
    trackedPaths
  }
}
